<template>
  <controller>
    <main class="catalog-page">
      <page-header title="Catálogo de Categorias e Rubricas">
        <p class="catalog-description">Consulte as categorias, rubricas e parâmetros usados nos fluxos de atividades.</p>
      </page-header>

      <section class="catalog-content">
        <section class="filters" aria-label="Filtros do catálogo">
          <header class="filters-header">
            <div>
              <h2>Filtrar catálogo</h2>
              <p>Refine os resultados por texto, classificação, indicador ou status.</p>
            </div>
            <span v-if="activeFilterCount" class="active-filter-count">{{ activeFilterCount }} filtro(s) aplicado(s)</span>
          </header>

          <div class="filter-grid">
            <label class="search-filter">
              <span>Busca</span>
              <input v-model.trim="filters.query" type="search"
                placeholder="Categoria, rubrica ou indicador"
                autocomplete="off">
            </label>
            <label>
              <span>Classificação</span>
              <select v-model="filters.classificacao">
                <option value="">Todas as classificações</option>
                <option v-for="item in classificacoes" :key="item" :value="item">{{ item }}</option>
              </select>
            </label>
            <label>
              <span>Indicador</span>
              <select v-model="filters.indicador">
                <option value="">Todos os indicadores</option>
                <option v-for="item in indicadores" :key="item" :value="item">{{ item }}</option>
              </select>
            </label>
            <label>
              <span>Status da rubrica</span>
              <select v-model="filters.status">
                <option value="">Todos os status</option>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="sem-rubrica">Sem rubrica</option>
              </select>
            </label>
            <button type="button" class="clear-filters" :disabled="!activeFilterCount" @click="clearFilters">
              Limpar filtros
            </button>
          </div>
        </section>

        <form-spinner v-if="loading" aria-label="Carregando catálogo" />
        <section v-else-if="error" class="catalog-message error" role="alert">
          <strong>Não foi possível carregar o catálogo</strong>
          <p>{{ error }}</p>
          <button type="button" class="btn btn-primary" @click="loadCatalogo">Tentar novamente</button>
        </section>

        <template v-else>
          <header class="results-header" aria-live="polite">
            <div>
              <h2>Resultados</h2>
              <p>{{ resultSummary }}</p>
            </div>
          </header>

          <div v-if="filteredCategorias.length" class="table-wrap" tabindex="0" aria-label="Tabela de categorias e rubricas">
            <table>
              <caption class="sr-only">Categorias, rubricas e parâmetros encontrados no catálogo</caption>
              <thead>
                <tr>
                  <th scope="col">Categoria</th>
                  <th scope="col">Rubrica</th>
                  <th scope="col">Classificação</th>
                  <th scope="col">Indicador</th>
                  <th scope="col">Público-alvo</th>
                  <th scope="col">Permissões por cargo</th>
                  <th scope="col">SLA</th>
                  <th scope="col">Envia NPS</th>
                  <th scope="col">Instruções</th>
                  <th scope="col">Status Rubrica</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="categoria in filteredCategorias" :key="categoria.idAtividadeCategoria">
                  <td data-label="Categoria"><strong class="category-name">{{ categoria.descricaoCategoriaAtividade || '-' }}</strong></td>
                  <td data-label="Rubrica">
                    <router-link v-if="categoria.idFormulario"
                      :to="`/atividades/formularios/view/${categoria.idFormulario}`"
                      class="rubrica-link" target="_blank" rel="noopener">
                      {{ categoria.formulario || '-' }} ↗
                    </router-link>
                    <span v-else>-</span>
                  </td>
                  <td data-label="Classificação">
                    <span v-if="categoria.descricaoClassificacaoAtividade" class="classification-badge">
                      {{ categoria.descricaoClassificacaoAtividade }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td data-label="Indicador">{{ categoria.descricaoIndicador || '-' }}</td>
                  <td data-label="Público-alvo">{{ publicoAlvo(categoria.idPerfilPessoa) }}</td>
                  <td data-label="Permissões por cargo">
                    <ul v-if="categoria.permissions.length" class="permission-list">
                      <li v-for="permission in categoria.permissions" :key="permission.idFuncao">
                        <strong>{{ permission.nome }}</strong>
                        <span v-if="isTruthy(permission.view)" class="permission-badge view">Visualizar</span>
                        <span v-if="isTruthy(permission.execute)" class="permission-badge execute">Executar</span>
                      </li>
                    </ul>
                    <span v-else class="no-permission">Nenhuma permissão</span>
                  </td>
                  <td data-label="SLA">{{ formatSla(categoria.horasSLA) }}</td>
                  <td data-label="Envia NPS">{{ isTruthy(categoria.enviaNps) ? 'Sim' : 'Não' }}</td>
                  <td data-label="Instruções" class="instructions-cell">
                    <span :class="{ expanded: expandedInstructions[categoria.idAtividadeCategoria] }">
                      {{ categoria.instrucoes || '-' }}
                    </span>
                    <button v-if="hasLongInstructions(categoria.instrucoes)" type="button"
                      @click="toggleInstructions(categoria.idAtividadeCategoria)">
                      {{ expandedInstructions[categoria.idAtividadeCategoria] ? 'Ver menos' : 'Ver mais' }}
                    </button>
                  </td>
                  <td data-label="Status Rubrica">
                    <span :class="['status-badge', categoria.rubricaStatus]">
                      {{ statusLabel(categoria.rubricaStatus) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <section v-else class="catalog-message">
            <strong>Nenhum resultado encontrado</strong>
            <p>Revise os termos da busca ou limpe os filtros para consultar todo o catálogo.</p>
            <button v-if="activeFilterCount" type="button" class="btn btn-secondary" @click="clearFilters">Limpar filtros</button>
          </section>
        </template>
      </section>
    </main>
  </controller>
</template>

<script>
import AtividadesService from '@/services/atividades.service.js'
import FormulariosService from '@/services/formularios.service.js'
import FormSpinner from '@/components/utilities/FormSpinner.vue'

const normalize = value => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()

export default {
  name: 'CatalogoRubricas',
  components: { FormSpinner },
  data () {
    return {
      categorias: [],
      error: null,
      expandedInstructions: {},
      filters: { query: '', classificacao: '', indicador: '', status: 'ativo' },
      loading: false
    }
  },
  computed: {
    classificacoes () {
      return this.uniqueSorted(this.categorias.map(item => item.descricaoClassificacaoAtividade))
    },
    indicadores () {
      return this.uniqueSorted(this.categorias.map(item => item.descricaoIndicador))
    },
    activeFilterCount () {
      return Object.values(this.filters).filter(Boolean).length
    },
    resultSummary () {
      const total = this.categorias.length
      const filtered = this.filteredCategorias.length

      return this.activeFilterCount
        ? `${filtered} de ${total} categoria(s) encontrada(s)`
        : `${total} categoria(s) encontrada(s)`
    },
    filteredCategorias () {
      const query = normalize(this.filters.query)

      return this.categorias.filter(categoria => {
        const searchable = [
          categoria.descricaoCategoriaAtividade,
          categoria.formulario,
          categoria.descricaoClassificacaoAtividade,
          categoria.descricaoIndicador,
          categoria.instrucoes,
          this.publicoAlvo(categoria.idPerfilPessoa),
          ...(categoria.permissions || []).map(permission => permission.nome)
        ].map(normalize).join(' ')

        return (!query || searchable.includes(query)) &&
          (!this.filters.classificacao || categoria.descricaoClassificacaoAtividade === this.filters.classificacao) &&
          (!this.filters.indicador || categoria.descricaoIndicador === this.filters.indicador) &&
          (!this.filters.status || categoria.rubricaStatus === this.filters.status)
      })
    }
  },
  mounted () {
    this.loadCatalogo()
  },
  methods: {
    async loadCatalogo () {
      this.loading = true
      this.error = null

      try {
        const [categoriasResponse, formulariosResponse] = await Promise.all([
          AtividadesService.getCategoriasAtividades(true),
          FormulariosService.getFormularios()
        ])
        const formularios = new Map(
          (formulariosResponse.data || []).map(item => [String(item.idFormulario), item])
        )

        this.categorias = (categoriasResponse.data || []).map(categoria => {
          const formulario = formularios.get(String(categoria.idFormulario))
          const rubricaStatus = formulario
            ? (this.isTruthy(formulario.ativo) ? 'ativo' : 'inativo')
            : 'sem-rubrica'

          return {
            ...categoria,
            formulario: categoria.formulario || formulario?.descricao || null,
            permissions: (categoria.permissions || []).filter(permission => this.isTruthy(permission.view) || this.isTruthy(permission.execute)),
            rubricaStatus
          }
        })
      } catch (error) {
        this.error = error.response?.data?.message ||
          'Não foi possível carregar o catálogo de categorias e rubricas.'
      } finally {
        this.loading = false
      }
    },
    clearFilters () {
      this.filters = { query: '', classificacao: '', indicador: '', status: '' }
    },
    uniqueSorted (values) {
      return [...new Set(values.filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, 'pt-BR'))
    },
    publicoAlvo (idPerfilPessoa) {
      return { 1: 'Aluno', 2: 'Colaborador', 3: 'Responsável' }[Number(idPerfilPessoa)] || '-'
    },
    formatSla (horas) {
      return horas === null || horas === undefined || horas === '' ? '-' : `${horas}h`
    },
    isTruthy (value) {
      return value === true || value === 1 || value === '1'
    },
    statusLabel (status) {
      return { ativo: 'Ativo', inativo: 'Inativo', 'sem-rubrica': 'Sem rubrica' }[status]
    },
    hasLongInstructions (instructions) {
      return Boolean(instructions && instructions.length > 95)
    },
    toggleInstructions (id) {
      this.expandedInstructions[id] = !this.expandedInstructions[id]
    }
  }
}
</script>

<style lang="scss" scoped>
.catalog-page { background: $color-bg; min-height: 100%; }
.catalog-description { color: $color-text; margin: $hmg_mini 0 0; }
.catalog-content { margin: 0 auto; max-width: 1480px; padding: $hmg_mid; width: 100%; }
.filters {
  background: $color-secondary; border: $border-component; border-radius: $border-radius;
  margin-bottom: $hmg_mid; padding: $hmg_mid;
}
.filters-header, .results-header {
  align-items: flex-start; display: flex; gap: $mg_small; justify-content: space-between;
  h2 { color: $color-dev; font-size: $font-size-larger; margin: 0 0 $hmg_mini; }
  p { color: $color-text; margin: 0; }
}
.filter-grid {
  align-items: flex-end; display: grid; gap: $mg_mini;
  grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(180px, 1fr)) auto;
  margin-top: $hmg_small;

  label { display: flex; flex-direction: column; gap: $hmg_mini; margin: 0; min-width: 0; }
  label > span { color: $color-dev; font-size: $font-size-small; font-weight: 600; }
  input, select {
    background: $color-secondary; border: $border-component; border-radius: $border-radius-small;
    color: $color-dev; font: inherit; min-height: 44px; min-width: 0; padding: 0 $mg_mini; width: 100%;

    &:focus-visible {
      border-color: $color-primary !important; outline: 3px solid $color-warning-light; outline-offset: 1px;
    }
  }
}
.active-filter-count {
  background: $color-alert-light-hover; border-radius: $border-radius-xlarge; color: $color-primary;
  font-size: $font-size-small; font-weight: 700; padding: $hmg_mini $mg_mini; white-space: nowrap;
}
.clear-filters {
  background: $color-bg; border: $border-component; border-radius: $border-radius-small; color: $color-dev;
  cursor: pointer; font-weight: 600; min-height: 44px; padding: 0 $mg_small; white-space: nowrap;

  &:hover:not(:disabled), &:focus-visible { border-color: $color-primary !important; color: $color-primary; }
  &:focus-visible { outline: 3px solid $color-warning-light; outline-offset: 1px; }
  &:disabled { cursor: not-allowed; opacity: .5; }
}
.results-header { align-items: center; margin-bottom: $mg_mini; padding: 0 $hmg_mini; }
.table-wrap {
  @extend %scrollbar_card;
  background: $color-secondary; border: $border-component; border-radius: $border-radius;
  overflow-x: auto;

  &:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
}
table { border-collapse: collapse; min-width: 1520px; table-layout: fixed; width: 100%; }
th, td { font-size: $font-size-small; padding: $mg_small; text-align: left; vertical-align: top; }
th {
  background: $color-bg; border-bottom: $border-component; color: $color-text;
  font-size: $font-size-small; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
}
th:nth-child(1) { width: 16%; } th:nth-child(2) { width: 14%; }
th:nth-child(3) { width: 9%; } th:nth-child(4) { width: 9%; }
th:nth-child(5) { width: 7%; } th:nth-child(6) { width: 19%; }
th:nth-child(7) { width: 5%; } th:nth-child(8) { width: 6%; }
th:nth-child(9) { width: 10%; } th:nth-child(10) { width: 7%; }
td {
  border-bottom: $border-component; color: $color-dev; line-height: 1.45;
  &:nth-child(7), &:nth-child(8), &:nth-child(10) { white-space: nowrap; }
}
tbody tr {
  transition: background-color .15s ease;
  &:hover { background: $color-alert-light-hover; }
  &:last-child td { border-bottom: 0; }
}
.category-name { font-weight: 600; }
.permission-list { display: grid; gap: $hmg_mini; list-style: none; margin: 0; padding: 0; }
.permission-list li { align-items: center; display: flex; flex-wrap: wrap; gap: $hmg_mini; }
.permission-list strong { flex-basis: 100%; font-size: $font-size-small; font-weight: 600; }
.permission-badge {
  border-radius: $border-radius-xlarge; display: inline-flex; font-size: 11px;
  font-weight: 700; line-height: 1; padding: 4px 7px;

  &.view { background: $color-bg; color: $color-execute; }
  &.execute { background: $color-active-light; color: $color-dev; }
}
.no-permission { color: $color-text; font-size: $font-size-small; }
.rubrica-link {
  color: $color-primary; font-weight: 600; text-decoration: none;

  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 3px solid $color-warning-light; outline-offset: 2px; }
}
.classification-badge, .status-badge {
  border-radius: $border-radius-xlarge; display: inline-flex; font-size: $font-size-small;
  font-weight: 700; line-height: 1.2; padding: $hmg_mini $mg_mini;
}
.classification-badge { background: $color-bg; color: $color-execute; }
.status-badge {
  &.ativo { background: $color-active-light; color: $color-dev; }
  &.inativo { background: $color-bg; color: $color-text; }
  &.sem-rubrica { background: $color-warning; color: $color-dev; }
}
.instructions-cell {
  span {
    display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2;
    &.expanded { display: block; overflow: visible; }
  }
  button {
    background: transparent; border: 0; color: $color-primary; cursor: pointer;
    font-size: $font-size-small; font-weight: 600; margin-top: $hmg_mini; padding: 0;

    &:focus-visible { outline: 3px solid $color-warning-light; outline-offset: 2px; }
  }
}
.catalog-message {
  background: $color-secondary; border: $border-component; border-radius: $border-radius;
  color: $color-text; padding: $mg; text-align: center;

  strong { color: $color-dev; display: block; font-size: $font-size-larger; }
  &.error { border-color: $color-error !important; }
  p { margin: $hmg_mini 0 $mg_small; }
}
.sr-only {
  clip: rect(0, 0, 0, 0); clip-path: inset(50%); height: 1px; overflow: hidden;
  position: absolute; white-space: nowrap; width: 1px;
}
@media (max-width: 1180px) {
  .filter-grid { grid-template-columns: repeat(2, minmax(220px, 1fr)); }
  .clear-filters { justify-self: start; }
}
@media (max-width: 700px) {
  .catalog-content { padding: $mg_small; }
  .filters { padding: $mg_small; }
  .filters-header { align-items: stretch; flex-direction: column; }
  .active-filter-count { align-self: flex-start; }
  .filter-grid { grid-template-columns: 1fr; }
  .clear-filters { justify-self: stretch; width: 100%; }
  .results-header { padding: 0; }
  .table-wrap { border: 0 !important; overflow: visible; }
  table, tbody, tr, td { display: block; width: 100%; }
  table { min-width: 0; }
  thead { display: none; }
  tbody { display: grid; gap: $mg_mini; }
  tbody tr {
    background: $color-secondary; border: $border-component; border-radius: $border-radius-mid;
    overflow: hidden; padding: $hmg_mini $mg_small;

    &:hover { background: $color-secondary; }
  }
  td {
    align-items: flex-start; display: grid; gap: $mg_mini;
    grid-template-columns: minmax(105px, .8fr) minmax(0, 1.5fr);
    padding: $mg_mini 0; white-space: normal !important;

    &::before {
      color: $color-text; content: attr(data-label); font-size: $font-size-small;
      font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    }
    &:last-child { border-bottom: 0 !important; }
  }
  .classification-badge, .status-badge { justify-self: start; }
}
@media (prefers-reduced-motion: reduce) {
  tbody tr { transition: none; }
}
</style>
